import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';

const DASHBOARD_SERVICE_MAP = {
  DashboardPowerBasic: {
    id: 'DashboardPowerBasic',
    title: 'Power Basic Dashboard',
    path: '/dashboards/power-basic',
    icon: null,
    description: 'View power consumption and trends.',
  },
  DashboardHeatBasic: {
    id: 'DashboardHeatBasic',
    title: 'Heat Basic Dashboard',
    path: '/dashboards/heat-basic',
    icon: null,
    description: 'View heat consumption and trends.',
  },
  DashboardWaterBasic: {
    id: 'DashboardWaterBasic',
    title: 'Water Basic Dashboard',
    path: '/dashboards/water-basic',
    icon: null,
    description: 'View water consumption and trends.',
  },
};

/**
 * Hook: useDashboardsFromServices
 * - Reads accountId from route params (useParams())
 * - Fetches account via useGetAccountByIdQuery
 * - Maps account Services to dashboard entries using DASHBOARD_SERVICE_MAP
 *
 * Returns: { dashboards, isLoading, isFetching, error, account }
 */
export default function useDashboardsFromServices() {
  const { accountId } = useParams();

  const {
    data: account,
    isLoading,
    isFetching,
    error,
  } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });

  const dashboards = useMemo(() => {
    const services = Array.isArray(account?.Services) ? account.Services : [];
    const serviceNames = new Set(
      services.filter((s) => DASHBOARD_SERVICE_MAP[s.Name]).map((s) => s.Name),
    );

    return Object.values(DASHBOARD_SERVICE_MAP).filter((service) =>
      serviceNames.has(service.id),
    );
  }, [account?.Services]);

  return { dashboards, isLoading, isFetching, error, account };
}
